import React, { useCallback } from 'react';
import { CapturePropertyRobotDTO } from 'business/models/Models';
import { Field, FrapConfigType } from '../../types';
import RobotSettingsInput from '../robotSettingsInput';
import {
  getDropAreaField,
  getDropAreaFields,
  getDropIntensityField,
  getThresholdValuesFields,
} from '../../utils';
import { FieldTypes, frapPrefix } from '../../constants';

interface FRAPBleachingProps {
  robotCaptureProperties: CapturePropertyRobotDTO;
  robotId: number;
  handleChange: (propertyName: string, value: any) => void;
  handleFieldToggle: (propertyName: string, value?: any) => void;
  enabledFields: string[];
  lightPath: string;
  frapConfig: FrapConfigType;
}

function FRAPBleaching(props: FRAPBleachingProps) {
  const {
    robotCaptureProperties,
    robotId,
    handleChange,
    handleFieldToggle,
    enabledFields,
    lightPath,
    frapConfig,
  } = props;
  const prefix = lightPath?.toUpperCase() === 'FRAP' ? frapPrefix : '';

  const pulseField: Field = {
    DisplayLabel: 'Pulse Laser for',
    UnitLabel: 'ms',
    PropertyName: prefix + 'FrapLaserBleachTimeMs',
    IsMandatory: false,
    FieldType: 4,
    EnableRules: [],
    VisibleRules: [],
    NumericFieldProperty: null,
    SingleOptionFieldProperty: null,
  };

  const toggleField = useCallback(
    (propertyName: string, value?: any) => {
      const defaultValue = frapConfig?.[propertyName];
      if (value === null || value === undefined || value === 'NaN') {
        handleFieldToggle(propertyName, defaultValue);
      } else {
        if (propertyName === `${prefix}FrapSetDropAreaToIncludeForRandomBleachPosition`) {
          handleChange(`${prefix}FrapApplyRandomBleachForCenterDropArea`, false);
          handleChange(`${prefix}FrapApplyRandomBleachForNearEdgeDropArea`, false);
        }
        handleFieldToggle(propertyName, value);
      }
    },
    [handleChange, handleFieldToggle, frapConfig, prefix],
  );

  return (
    <>
      <div id="frap-bleaching-options-wrapper" className="border p-2">
        <legend id="frap-bleaching-options-title" className="text-lg">
          Bleach Options
        </legend>
        <RobotSettingsInput
          key={1}
          field={pulseField}
          value={robotCaptureProperties.data[robotId]?.[pulseField.PropertyName]}
          onChange={handleChange}
          onToggle={handleFieldToggle}
          enabled={enabledFields.includes(pulseField.PropertyName)}
          formData={robotCaptureProperties.data[robotId]}
          placeholder={frapConfig?.[pulseField.PropertyName]}
          defaultValue={frapConfig?.[pulseField.PropertyName]}
        />

        <RobotSettingsInput
          key={2}
          field={getDropAreaField(prefix)}
          value={
            robotCaptureProperties.data[robotId]?.[getDropAreaField(prefix).PropertyName]
          }
          onChange={handleChange}
          onToggle={toggleField}
          enabled={enabledFields.includes(getDropAreaField(prefix).PropertyName)}
          formData={robotCaptureProperties.data[robotId]}
          customClassName="!w-[100%] mt-2"
        />
        <fieldset
          id="frap-bleaching-options-fieldset"
          className="flex flex-row mb-2 ml-3"
        >
          {getDropAreaFields(prefix).map((ff, _idx) => (
            <div
              id={`frap-bleaching-options-fieldset-robotsettingsinput-${_idx}`}
              className="flex flex-row ml-2"
              key={_idx}
            >
              <RobotSettingsInput
                field={ff}
                value={robotCaptureProperties.data[robotId]?.[ff.PropertyName]}
                onChange={handleChange}
                onToggle={handleFieldToggle}
                enabled={enabledFields.includes(ff.PropertyName)}
                formData={robotCaptureProperties.data[robotId]}
                placeholder={frapConfig?.[ff.PropertyName]}
                defaultValue={
                  ff.FieldType === FieldTypes.Numeric
                    ? frapConfig?.[ff.PropertyName]
                    : undefined
                }
                customClassName="!w-[60%]"
              />
            </div>
          ))}
        </fieldset>
      </div>
      <div id="frap-bleaching-positionlocator-wrapper" className="border p-2">
        <legend id="frap-bleaching-positionlocator-title" className="text-lg">
          Bleach Position Locator
        </legend>
        <RobotSettingsInput
          key={1}
          field={getDropIntensityField(prefix)}
          value={
            robotCaptureProperties.data[robotId]?.[
              getDropIntensityField(prefix).PropertyName
            ]
          }
          onChange={handleChange}
          onToggle={handleFieldToggle}
          enabled={enabledFields.includes(getDropIntensityField(prefix).PropertyName)}
          formData={robotCaptureProperties.data[robotId]}
          placeholder={frapConfig?.[getDropIntensityField(prefix).PropertyName]}
          customClassName="!w-full"
        />
        <fieldset
          id="frap-bleaching-positionlocator-fieldset"
          disabled={!enabledFields.includes(getDropIntensityField(prefix).PropertyName)}
        >
          {getThresholdValuesFields(prefix).map((ff, _idx) => (
            <div
              id={`frap-bleaching-positionlocator-fieldset-robotsettingsinput-${_idx}`}
              className="flex flex-row gap-2"
              key={_idx}
            >
              <RobotSettingsInput
                field={ff}
                value={robotCaptureProperties.data[robotId]?.[ff.PropertyName]}
                onChange={handleChange}
                onToggle={handleFieldToggle}
                enabled={enabledFields.includes(ff.PropertyName)}
                formData={robotCaptureProperties.data[robotId]}
                defaultValue={frapConfig?.[ff.PropertyName]}
                customClassName="!w-[80%]"
              />
              <span
                id={`frap-bleaching-positionlocator-fieldset-${ff.PropertyName}-text`}
              >
                {robotCaptureProperties.data[robotId]?.[ff.PropertyName]}
              </span>
            </div>
          ))}
        </fieldset>
      </div>
    </>
  );
}

export default FRAPBleaching;
